import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Space,
  Button,
  DatePicker,
  Typography,
  Row,
  Col,
  Statistic,
  Divider,
  message,
  Tabs,
  Tag,
  Progress
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import type { ColumnsType } from 'antd/es/table';
import invoiceService, {
  ARAgingReportData,
  ARAgingReportByCustomer,
  ARAgingReportDetail
} from '@/services/invoiceService';
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

const ARAgingReport: React.FC = () => {
    const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ARAgingReportData | null>(null);
  const [asOfDate, setAsOfDate] = useState<Dayjs | null>(dayjs());
  // Default cutoff: only include invoices issued on/after this date. Pinned
  // to 2026-04-01 so the report no longer pulls in years of legacy SENT rows
  // that were paid offline / never flipped to PAID.
  const [invoiceDateFrom, setInvoiceDateFrom] = useState<Dayjs | null>(dayjs('2026-04-01'));
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const data = await invoiceService.getAgingReport(
        asOfDate?.format('YYYY-MM-DD'),
        invoiceDateFrom?.format('YYYY-MM-DD')
      );
      setReportData(data);
    } catch (error: any) {
      message.error(error.message || t('Failed to fetch AR aging report'));
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date: Dayjs | null) => {
    setAsOfDate(date);
  };

  const handleInvoiceDateFromChange = (date: Dayjs | null) => {
    setInvoiceDateFrom(date);
  };

  const handleRefresh = () => {
    fetchReport();
  };

  const handleExport = () => {
    if (!reportData || reportData.details.length === 0) {
      message.warning(t('No data to export'));
      return;
    }
    const wb = XLSX.utils.book_new();
    // Sheet 1: By Customer
    const custRows = reportData.byCustomer.map(c => ({
      'Customer': c.customerName || '',
      'Code': c.customerCode || '',
      'Current': Number(c.current || 0),
      '1-30 Days': Number(c.days30 || 0),
      '31-60 Days': Number(c.days60 || 0),
      '90+ Days': Number(c.days90Plus || 0),
      'Total': Number(c.total || 0),
    }));
    const ws1 = XLSX.utils.json_to_sheet(custRows);
    ws1['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'By Customer');
    // Sheet 2: Details
    const detRows = reportData.details.map(d => ({
      'Invoice No': d.invoiceNo || '',
      'Customer': d.customerName || '',
      'Invoice Date': d.invoiceDate ? dayjs(d.invoiceDate).format('YYYY-MM-DD') : '',
      'Due Date': d.dueDate ? dayjs(d.dueDate).format('YYYY-MM-DD') : '',
      'Amount': Number(d.amountTotal || 0),
      'Balance': Number(d.amountResidual || 0),
      'Days Overdue': d.daysOverdue || 0,
      'Aging Bucket': d.agingBucket || '',
    }));
    const ws2 = XLSX.utils.json_to_sheet(detRows);
    ws2['!cols'] = [{ wch: 14 }, { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Details');
    XLSX.writeFile(wb, `AR_Aging_Report_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    message.success(t('AR Aging Report exported'));
  };

  const generatePrintContent = () => {
    const summary = reportData?.summary || {
      current: 0,
      days30: 0,
      days60: 0,
      days90Plus: 0,
      total: 0
    };

    return `
      <div class="print-header">
        <div class="header-left">
          <img src="${window.location.origin}/shoptoplus.png" alt="Company Logo" class="logo" />
          <div class="company-info">
            <div class="company-name">Shop To Plus Limited</div>
            <div>Unit 1234, Some Building</div>
            <div>Hong Kong</div>
          </div>
        </div>
        <div class="header-right">
          <div class="report-title">AR AGING REPORT</div>
          <div class="report-date">As of: ${asOfDate?.format('YYYY-MM-DD') || 'N/A'}</div>
          <div class="print-date">Printed: ${new Date().toLocaleDateString()}</div>
        </div>
      </div>

      <div class="summary-section">
        <h3>Aging Summary</h3>
        <table class="summary-table">
          <tr>
            <td class="label">Current:</td>
            <td class="value current">${formatCurrency(summary.current)}</td>
            <td class="label">1-30 Days:</td>
            <td class="value days30">${formatCurrency(summary.days30)}</td>
            <td class="label">31-60 Days:</td>
            <td class="value days60">${formatCurrency(summary.days60)}</td>
          </tr>
          <tr>
            <td class="label">90+ Days:</td>
            <td class="value days90">${formatCurrency(summary.days90Plus)}</td>
            <td class="label" colspan="2"><strong>Total Outstanding:</strong></td>
            <td class="value total" colspan="2"><strong>${formatCurrency(summary.total)}</strong></td>
          </tr>
        </table>
      </div>

      <div class="section">
        <h3>Summary by Customer</h3>
        <table class="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th class="number">Current</th>
              <th class="number">1-30 Days</th>
              <th class="number">31-60 Days</th>
              <th class="number">90+ Days</th>
              <th class="number">Total</th>
            </tr>
          </thead>
          <tbody>
            ${(reportData?.byCustomer || []).map(row => `
              <tr>
                <td>
                  <strong>${row.customerName}</strong><br/>
                  <small>${row.customerCode}</small>
                </td>
                <td class="number">${formatCurrency(row.current)}</td>
                <td class="number">${formatCurrency(row.days30)}</td>
                <td class="number">${formatCurrency(row.days60)}</td>
                <td class="number">${formatCurrency(row.days90Plus)}</td>
                <td class="number"><strong>${formatCurrency(row.total)}</strong></td>
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td><strong>Grand Total</strong></td>
              <td class="number"><strong>${formatCurrency(summary.current)}</strong></td>
              <td class="number"><strong>${formatCurrency(summary.days30)}</strong></td>
              <td class="number"><strong>${formatCurrency(summary.days60)}</strong></td>
              <td class="number"><strong>${formatCurrency(summary.days90Plus)}</strong></td>
              <td class="number"><strong>${formatCurrency(summary.total)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="section page-break">
        <h3>Invoice Details</h3>
        <table class="data-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Customer</th>
              <th>Invoice Date</th>
              <th>Due Date</th>
              <th class="number">Original</th>
              <th class="number">Outstanding</th>
              <th class="number">Days Overdue</th>
              <th>Bucket</th>
            </tr>
          </thead>
          <tbody>
            ${(reportData?.details || []).map(row => `
              <tr>
                <td><code>${row.invoiceNo}</code></td>
                <td>${row.customerName}</td>
                <td>${new Date(row.invoiceDate).toLocaleDateString()}</td>
                <td>${row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '-'}</td>
                <td class="number">${formatCurrency(row.amountTotal)}</td>
                <td class="number"><strong>${formatCurrency(row.amountResidual)}</strong></td>
                <td class="number ${row.daysOverdue > 0 ? 'overdue' : ''}">${row.daysOverdue > 0 ? row.daysOverdue : 'Current'}</td>
                <td><span class="bucket bucket-${row.agingBucket}">${getBucketLabel(row.agingBucket)}</span></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  };

  const getBucketLabel = (bucket: string) => {
    switch (bucket) {
      case 'current': return 'Current';
      case 'days30': return '1-30 Days';
      case 'days60': return '31-60 Days';
      case 'days90Plus': return '90+ Days';
      default: return bucket;
    }
  };

  const handlePrint = () => {
    if (!reportData) {
      message.warning(t('No report data to print'));
      return;
    }

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const printContent = generatePrintContent();
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>AR Aging Report - ${asOfDate?.format('YYYY-MM-DD')}</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 15mm;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 10px;
              line-height: 1.4;
              color: #333;
            }
            .print-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding-bottom: 15px;
              border-bottom: 3px solid #00bcd4;
              margin-bottom: 20px;
            }
            .header-left {
              display: flex;
              align-items: center;
              gap: 15px;
            }
            .logo {
              width: 60px;
              height: auto;
            }
            .company-info {
              font-size: 10px;
            }
            .company-name {
              font-size: 14px;
              font-weight: bold;
              color: #1890ff;
            }
            .header-right {
              text-align: right;
            }
            .report-title {
              font-size: 20px;
              font-weight: bold;
              color: #1890ff;
            }
            .report-date {
              font-size: 12px;
              margin-top: 5px;
            }
            .print-date {
              font-size: 10px;
              color: #666;
            }
            .summary-section {
              margin-bottom: 20px;
              padding: 15px;
              background: #f5f5f5;
              border-radius: 4px;
            }
            .summary-section h3 {
              margin-bottom: 10px;
              font-size: 12px;
              color: #333;
            }
            .summary-table {
              width: 100%;
              border-collapse: collapse;
            }
            .summary-table td {
              padding: 5px 10px;
            }
            .summary-table .label {
              font-weight: 500;
              width: 100px;
            }
            .summary-table .value {
              font-weight: bold;
              width: 120px;
            }
            .summary-table .current { color: #52c41a; }
            .summary-table .days30 { color: #faad14; }
            .summary-table .days60 { color: #fa8c16; }
            .summary-table .days90 { color: #f5222d; }
            .summary-table .total { color: #1890ff; }
            .section {
              margin-bottom: 20px;
            }
            .section h3 {
              font-size: 12px;
              margin-bottom: 10px;
              padding-bottom: 5px;
              border-bottom: 1px solid #ddd;
            }
            .data-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 9px;
            }
            .data-table th,
            .data-table td {
              border: 1px solid #ddd;
              padding: 6px 8px;
              text-align: left;
            }
            .data-table th {
              background: #fafafa;
              font-weight: bold;
            }
            .data-table .number {
              text-align: right;
            }
            .data-table tbody tr:nth-child(even) {
              background: #fafafa;
            }
            .data-table .total-row {
              background: #e6f7ff !important;
            }
            .data-table code {
              background: #f5f5f5;
              padding: 2px 4px;
              border-radius: 2px;
              font-family: monospace;
            }
            .data-table small {
              color: #666;
              font-size: 8px;
            }
            .overdue {
              color: #f5222d;
            }
            .bucket {
              padding: 2px 6px;
              border-radius: 3px;
              font-size: 8px;
              white-space: nowrap;
            }
            .bucket-current { background: #f6ffed; color: #52c41a; }
            .bucket-days30 { background: #fff7e6; color: #fa8c16; }
            .bucket-days60 { background: #fff2e8; color: #fa541c; }
            .bucket-days90Plus { background: #fff1f0; color: #f5222d; }
            .page-break {
              page-break-before: auto;
            }
            thead {
              display: table-header-group;
            }
            tr {
              page-break-inside: avoid;
            }
            .footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              text-align: center;
              font-size: 9px;
              color: #666;
              padding: 10px;
              border-top: 1px solid #ddd;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
          <div class="footer">
            This is a computer-generated report and does not require a signature. | Page <span class="pageNumber"></span>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getAgingBucketTag = (bucket: string) => {
    switch (bucket) {
      case 'current':
        return <Tag color="green">{t('Current')}</Tag>;
      case 'days30':
        return <Tag color="orange">1-30 Days</Tag>;
      case 'days60':
        return <Tag color="volcano">31-60 Days</Tag>;
      case 'days90Plus':
        return <Tag color="red">90+ Days</Tag>;
      default:
        return <Tag>{bucket}</Tag>;
    }
  };

  // Summary by customer columns
  const customerColumns: ColumnsType<ARAgingReportByCustomer> = [
    {
      title: t('Customer'),
      key: 'customer',
      width: 250,
      render: (_, record) => (
        <div>
          <Text strong>{record.customerName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.customerCode}
          </Text>
        </div>
      ),
      sorter: (a, b) => a.customerName.localeCompare(b.customerName)
    },
    {
      title: t('Current'),
      dataIndex: 'current',
      key: 'current',
      width: 120,
      align: 'right',
      render: (value) => (
        <Text style={{ color: value > 0 ? '#52c41a' : undefined }}>
          {formatCurrency(value)}
        </Text>
      ),
      sorter: (a, b) => a.current - b.current
    },
    {
      title: '1-30 Days',
      dataIndex: 'days30',
      key: 'days30',
      width: 120,
      align: 'right',
      render: (value) => (
        <Text style={{ color: value > 0 ? '#faad14' : undefined }}>
          {formatCurrency(value)}
        </Text>
      ),
      sorter: (a, b) => a.days30 - b.days30
    },
    {
      title: '31-60 Days',
      dataIndex: 'days60',
      key: 'days60',
      width: 120,
      align: 'right',
      render: (value) => (
        <Text style={{ color: value > 0 ? '#fa8c16' : undefined }}>
          {formatCurrency(value)}
        </Text>
      ),
      sorter: (a, b) => a.days60 - b.days60
    },
    {
      title: '90+ Days',
      dataIndex: 'days90Plus',
      key: 'days90Plus',
      width: 120,
      align: 'right',
      render: (value) => (
        <Text style={{ color: value > 0 ? '#f5222d' : undefined }}>
          {formatCurrency(value)}
        </Text>
      ),
      sorter: (a, b) => a.days90Plus - b.days90Plus
    },
    {
      title: t('Total'),
      dataIndex: 'total',
      key: 'total',
      width: 130,
      align: 'right',
      render: (value) => <Text strong>{formatCurrency(value)}</Text>,
      sorter: (a, b) => a.total - b.total
    }
  ];

  // Detail columns
  const detailColumns: ColumnsType<ARAgingReportDetail> = [
    {
      title: t('Invoice / CN #'),
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 160,
      render: (value: string, record: any) => {
        const isCN = value?.startsWith('CN') || (record?.amountResidual ?? 0) < 0;
        return (
          <span style={{ fontStyle: isCN ? 'italic' : 'normal' }}>
            {isCN && <Tag color="orange" style={{ marginRight: 6 }}>CN</Tag>}
            <Text code>{value}</Text>
          </span>
        );
      }
    },
    {
      title: t('Customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 200,
      sorter: (a, b) => a.customerName.localeCompare(b.customerName)
    },
    {
      title: t('Invoice Date'),
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      width: 120,
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a, b) => new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime()
    },
    {
      title: t('Due Date'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
      sorter: (a, b) => {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      }
    },
    {
      title: t('Original Amount'),
      dataIndex: 'amountTotal',
      key: 'amountTotal',
      width: 130,
      align: 'right',
      render: (value) => formatCurrency(value),
      sorter: (a, b) => a.amountTotal - b.amountTotal
    },
    {
      title: t('Outstanding'),
      dataIndex: 'amountResidual',
      key: 'amountResidual',
      width: 130,
      align: 'right',
      render: (value: number) => {
        const neg = value < 0;
        const abs = Math.abs(value);
        return (
          <Text strong style={{ color: neg ? '#cf1322' : undefined, fontStyle: neg ? 'italic' : 'normal' }}>
            {neg ? `-${formatCurrency(abs)}` : formatCurrency(value)}
          </Text>
        );
      },
      sorter: (a, b) => a.amountResidual - b.amountResidual
    },
    {
      title: t('Days Overdue'),
      dataIndex: 'daysOverdue',
      key: 'daysOverdue',
      width: 110,
      align: 'center',
      render: (days) => (
        <Text style={{ color: days > 0 ? '#f5222d' : '#52c41a' }}>
          {days > 0 ? days : t('Current')}
        </Text>
      ),
      sorter: (a, b) => a.daysOverdue - b.daysOverdue
    },
    {
      title: t('Aging Bucket'),
      dataIndex: 'agingBucket',
      key: 'agingBucket',
      width: 120,
      render: (bucket) => getAgingBucketTag(bucket),
      filters: [
        { text: t('Current'), value: 'current' },
        { text: '1-30 Days', value: 'days30' },
        { text: '31-60 Days', value: 'days60' },
        { text: '90+ Days', value: 'days90Plus' }
      ],
      onFilter: (value, record) => record.agingBucket === value
    }
  ];

  const summary = reportData?.summary || {
    current: 0,
    days30: 0,
    days60: 0,
    days90Plus: 0,
    total: 0
  };

  // Calculate percentages for progress bars
  const getPercentage = (value: number) => {
    if (summary.total === 0) return 0;
    return Math.round((value / summary.total) * 100);
  };

  const tabItems = [
    {
      key: 'summary',
      label: t('Summary by Customer'),
      children: (
        <Table
          dataSource={reportData?.byCustomer || []}
          columns={customerColumns}
          rowKey="customerId"
          loading={loading}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => t('Total {{total}} customers', { total })
          }}
          size="small"
          bordered
          summary={(pageData) => {
            const totals = pageData.reduce(
              (acc, row) => ({
                current: acc.current + row.current,
                days30: acc.days30 + row.days30,
                days60: acc.days60 + row.days60,
                days90Plus: acc.days90Plus + row.days90Plus,
                total: acc.total + row.total
              }),
              { current: 0, days30: 0, days60: 0, days90Plus: 0, total: 0 }
            );
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text strong>{t('Page Total')}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>{formatCurrency(totals.current)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Text strong>{formatCurrency(totals.days30)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong>{formatCurrency(totals.days60)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <Text strong>{formatCurrency(totals.days90Plus)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <Text strong>{formatCurrency(totals.total)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      )
    },
    {
      key: 'details',
      label: t('Invoice Details'),
      children: (
        <Table
          dataSource={reportData?.details || []}
          columns={detailColumns}
          rowKey="invoiceId"
          loading={loading}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => t('Total {{total}} invoices', { total })
          }}
          scroll={{ x: 1100 }}
          size="small"
          bordered
        />
      )
    }
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              <DollarOutlined style={{ marginRight: 8 }} />
              {t('AR Aging Report')}
                                      </Title>
            <Text type="secondary">
              {t('Accounts Receivable aging analysis by customer and invoice')}
                                      </Text>
          </Col>
          <Col>
            <Space>
              <DatePicker
                value={invoiceDateFrom}
                onChange={handleInvoiceDateFromChange}
                placeholder={t('From (invoice date)')}
                format="YYYY-MM-DD"
                allowClear
              />
              <DatePicker
                value={asOfDate}
                onChange={handleDateChange}
                placeholder={t('As of Date')}
                format="YYYY-MM-DD"
                allowClear={false}
              />
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                {t('Refresh')}
                                            </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={!reportData || reportData.details.length === 0}
              >
                {t('Export')}
                                            </Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={handlePrint}
                disabled={!reportData || reportData.details.length === 0}
              >
                {t('Print')}
                                            </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Summary Statistics */}
      <Card style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={4}>
            <Statistic
              title={t('Current')}
              value={summary.current}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#52c41a', fontSize: 20 }}
            />
            <Progress
              percent={getPercentage(summary.current)}
              strokeColor="#52c41a"
              showInfo={false}
              size="small"
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Statistic
              title={<><ClockCircleOutlined /> 1-30 Days</>}
              value={summary.days30}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#faad14', fontSize: 20 }}
            />
            <Progress
              percent={getPercentage(summary.days30)}
              strokeColor="#faad14"
              showInfo={false}
              size="small"
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Statistic
              title={<><WarningOutlined /> 31-60 Days</>}
              value={summary.days60}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#fa8c16', fontSize: 20 }}
            />
            <Progress
              percent={getPercentage(summary.days60)}
              strokeColor="#fa8c16"
              showInfo={false}
              size="small"
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Statistic
              title={<><ExclamationCircleOutlined /> 90+ Days</>}
              value={summary.days90Plus}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#f5222d', fontSize: 20 }}
            />
            <Progress
              percent={getPercentage(summary.days90Plus)}
              strokeColor="#f5222d"
              showInfo={false}
              size="small"
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Statistic
              title={t('Total Outstanding')}
              value={summary.total}
              precision={2}
              prefix="$"
              valueStyle={{ fontSize: 20, fontWeight: 'bold' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Data Tables */}
      <Card style={{ marginTop: 16 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>
    </div>
  );
};

export default ARAgingReport;